// src/modules/app-views/products/pages/Document/tabs/SuppliersTab/modals/SupplierModal/Step2.jsx
// *****************************************************************************
// SUPPLIER MODAL - STEP 2 (CUSTO)
// *****************************************************************************
// Cost composition and margin:
// Row 1: List Price | Chain Discount | Purchase Price (calculated)
// Row 2: Applied Costs table (add/edit/remove) — builds replacement price
// Row 3: Replacement Price (calculated) | NF Discount | Final Price
// Row 4: Conversion Factor | Converted Value | MarkUp %
// *****************************************************************************

// *****************************************************************************
// IMPORTS
// *****************************************************************************
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Form,
  Row,
  Col,
  Divider,
  Typography,
  Button,
  Tag,
  Popconfirm,
  Empty,
  Space,
} from 'antd';
import { DollarOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleMultiDiscount from 'components/SimpleComponents/SimpleMultiDiscount';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleTable from 'components/SimpleComponents/SimpleTable';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import CostItemModal from '../CostItemModal';

const { Text } = Typography;

// *****************************************************************************
// STEP CONTENT COMPONENT
// *****************************************************************************
const Step2Content = ({ appliedCosts, setAppliedCosts, productSalePrice = 0, productUnitUid = null }) => {
  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------
  const form = Form.useFormInstance();

  // ---------------------------------------------------------------------------
  // WATCH
  // ---------------------------------------------------------------------------
  const listPrice = Form.useWatch('listPrice') || 0;
  const chainDiscount = Form.useWatch('chainDiscount');
  const lastNfDiscountPercentage = Form.useWatch('lastNfDiscountPercentage') || 0;
  const conversionFactor = Form.useWatch('conversionFactor') || 1;
  const supplierUnitUid = Form.useWatch('unitUid');

  // Lock conversion factor to 1 when purchase unit matches product sale unit
  const isSameUnit = productUnitUid && supplierUnitUid && productUnitUid === supplierUnitUid;

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------
  const [showCostModal, setShowCostModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [editingCostIndex, setEditingCostIndex] = useState(null);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  // Focus listPrice on initial mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById('listPrice');
      if (el) el.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Force factor to 1 when units match
  useEffect(() => {
    if (isSameUnit && form) {
      form.setFieldValue('conversionFactor', 1);
    }
  }, [isSameUnit, form]);

  // Auto-calculate full price chain when any input changes
  // Flow: listPrice → purchasePrice → +costs → replacementPrice → -nfDiscount → finalPrice → /factor → convertedUnitCost → markup
  useEffect(() => {
    if (!form) return;

    // 1. Purchase price = list price - chain discount
    const discountPct = chainDiscount?.totalPercentage || 0;
    const purchasePrice = financialService.roundCurrency(
      listPrice * (1 - discountPct / 100),
    );

    // 2. Replacement price = purchase + costs + purchase taxes
    const composition = financialService.calculateCostComposition({
      purchasePrice,
      markup: 0,
      appliedCosts,
    });
    const replacementPrice = composition.replacementPrice;

    // 3. Final price = replacement - NF discount
    const finalPrice = financialService.roundCurrency(
      replacementPrice * (1 - (lastNfDiscountPercentage || 0) / 100),
    );

    // 4. Converted unit cost = final price / conversion factor
    const factor = conversionFactor || 1;
    const convertedUnitCost = factor > 0
      ? financialService.roundCurrency(finalPrice / factor)
      : finalPrice;

    // 5. Markup = (sale price - converted cost) / converted cost × 100
    const calculatedMarkup = convertedUnitCost > 0
      ? financialService.calculateMarkup(convertedUnitCost, productSalePrice, 3)
      : 0;

    form.setFieldsValue({
      purchasePrice,
      priceAfterChainDiscount: replacementPrice,
      priceAfterAllDiscounts: finalPrice,
      convertedUnitCost,
      markup: calculatedMarkup,
    });
  }, [listPrice, chainDiscount?.totalPercentage, appliedCosts, lastNfDiscountPercentage, conversionFactor, productSalePrice, form]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  const handleAddCost = useCallback(() => {
    setEditingCost(null);
    setEditingCostIndex(null);
    setShowCostModal(true);
  }, []);

  const handleEditCost = useCallback((cost, index) => {
    setEditingCost(cost);
    setEditingCostIndex(index);
    setShowCostModal(true);
  }, []);

  const handleRemoveCost = useCallback(
    index => {
      setAppliedCosts(prev => prev.filter((_, i) => i !== index));
    },
    [setAppliedCosts],
  );

  const handleCostModalConfirm = useCallback(
    costData => {
      if (editingCostIndex !== null) {
        setAppliedCosts(prev =>
          prev.map((c, i) => (i === editingCostIndex ? costData : c)),
        );
      } else {
        setAppliedCosts(prev => [...prev, costData]);
      }
      setShowCostModal(false);
      setEditingCost(null);
      setEditingCostIndex(null);
    },
    [editingCostIndex, setAppliedCosts],
  );

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // Compute display values for the table
  const purchasePrice = form?.getFieldValue('purchasePrice') || 0;
  const composition = useMemo(() => {
    return financialService.calculateCostComposition({
      purchasePrice,
      markup: 0,
      appliedCosts,
    });
  }, [purchasePrice, appliedCosts]);

  const costColumns = useMemo(
    () => [
      {
        title: translateX('name'),
        dataIndex: 'name',
        render: (val, record) => (
          <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
            <strong className='mr-2'>{val}</strong>
            <Tag
              color={getCostTypeConfig(record.type).color}
              style={{ fontSize: 9, lineHeight: '14px', padding: '0 3px', margin: 0 }}
            >
              {getCostTypeConfig(record.type).text}
            </Tag>
          </span>
        ),
      },
      {
        title: '%',
        dataIndex: 'percentage',
        width: 70,
        align: 'right',
        render: (val, record) => (
          <span style={{ fontSize: 11 }}>
            {record.mode === 'percentage'
              ? `${financialService.formatNumber(val, { decimals: 3 })}%`
              : '—'}
          </span>
        ),
      },
      {
        title: translateX('source'),
        dataIndex: 'payerSource',
        width: 80,
        align: 'center',
        render: val => {
          const labels = {
            municipal: translateX('payer_source_municipal'),
            state: translateX('payer_source_state'),
            federal: translateX('payer_source_federal'),
            other: translateX('payer_source_other'),
          };
          return <span style={{ fontSize: 11 }}>{labels[val] || val}</span>;
        },
      },
      {
        title: translateX('actions'),
        width: 60,
        align: 'center',
        render: (_, record, index) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditCost(record, index)}
            />
            <Popconfirm
              title={translateX('remove_cost_confirm')}
              onConfirm={() => handleRemoveCost(index)}
              okText={translateX('yes')}
              cancelText={translateX('no')}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [composition.appliedCosts, handleEditCost, handleRemoveCost],
  );

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Divider: Cost Composition */}
      {/*<Divider style={{ margin: '4px 0 8px' }}>*/}
      {/*  <Text type="secondary" style={{ fontSize: 12 }}>*/}
      {/*    <DollarOutlined style={{ marginRight: 4 }} />*/}
      {/*    {translateX('cost_composition')}*/}
      {/*  </Text>*/}
      {/*</Divider>*/}

      {/* Row 1: List Price | Chain Discount | Purchase Price */}
      <Row gutter={[16, 0]}>
        <Col xs={12} sm={8}>
          <SimpleCurrency name="listPrice" label="list_price" />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleMultiDiscount name="chainDiscount" label="chained_discount" base={listPrice} />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleCurrency name="purchasePrice" label="purchase_price" disabled />
        </Col>
      </Row>

      {/* Row 2: Applied Costs Table */}
      <Divider style={{ margin: '4px 0 8px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translateX('applied_costs')}
        </Text>
      </Divider>

      <Row justify="end" style={{ marginBottom: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={handleAddCost}>
          {translateX('add_cost')}
        </Button>
      </Row>

      {appliedCosts.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={translateX('no_costs_added')} />
      ) : (
        <div className="compact-cost-table">
          <SimpleTable
            columns={costColumns}
            dataSource={appliedCosts}
            rowKey={record => `${record.name}-${record.type}`}
            pagination={false}
            size="small"
            scroll={{ x: 400 }}
            onRow={(record, index) => ({
              onDoubleClick: () => handleEditCost(record, index),
            })}
          />
        </div>
      )}

      {/* Row 3: Replacement Cost | NF Discount | NF Price */}
      <Row gutter={[16, 0]} style={{ marginTop: 12 }}>
        <Col xs={12} sm={8}>
          <SimpleCurrency name="priceAfterChainDiscount" label="replacement_cost" disabled />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleNumber
            name="lastNfDiscountPercentage"
            label="last_nf_discount"
            decimalPlaces={2}
            min={0}
            max={100}
            percentage
          />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleCurrency name="priceAfterAllDiscounts" label="final_price" disabled />
        </Col>
      </Row>

      {/* Row 4: Conversion Factor | Converted Unit Value | Markup */}
      <Row gutter={[16, 0]}>
        <Col xs={12} sm={8}>
          <SimpleNumber
            name="conversionFactor"
            label="conversion_factor"
            min={isSameUnit ? 1 : 0.001}
            decimalPlaces={3}
            disabled={isSameUnit}
          />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleCurrency name="convertedUnitCost" label="converted_value" disabled />
        </Col>
        <Col xs={12} sm={8}>
          <SimpleNumber name="markup" label="markup" decimalPlaces={2} percentage disabled />
        </Col>
      </Row>

      {/* Row 5: Sale Price (temporary - will be removed later) */}
      {/*<Row gutter={[16, 0]}>*/}
      {/*  <Col xs={12} sm={8}>*/}
      {/*    <SimpleCurrency name="salePrice" label="sale_price" disabled />*/}
      {/*  </Col>*/}
      {/*</Row>*/}

      {/* Cost Item Modal */}
      <CostItemModal
        show={showCostModal}
        setShow={setShowCostModal}
        onConfirm={handleCostModalConfirm}
        editingItem={editingCost}
      />
    </>
  );
};

// *****************************************************************************
// STEP CONFIGURATION
// *****************************************************************************
const step2 = ({ appliedCosts, setAppliedCosts, productSalePrice = 0, productUnitUid = null }) => ({
  stepTitle: 'cost',
  validationFields: [],
  content: <Step2Content appliedCosts={appliedCosts} setAppliedCosts={setAppliedCosts} productSalePrice={productSalePrice} productUnitUid={productUnitUid} />,
  onEnter: ({ goFocus }) => goFocus?.('listPrice'),
});

// *****************************************************************************
// EXPORT
// *****************************************************************************
export default step2;

// Named export for direct use in Tabs mode (edit)
export { Step2Content };

// *****************************************************************************
// HELPERS
// *****************************************************************************

/**
 * Get cost type display config (tag text + color)
 */
function getCostTypeConfig(type) {
  const configs = {
    informative: { text: 'info', color: 'default' },
    cost: { text: 'custo', color: 'blue' },
    purchaseTax: { text: 'imp.compra', color: 'orange' },
    saleTax: { text: 'imp.venda', color: 'red' },
  };
  return configs[type] || configs.cost;
}
